import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { App as AntApp } from 'antd';
import AppBar from '../components/AppBar';
import { Skeletons, RM, RMk, initials } from '../components/kit';
import usePerformanceScopes from '../hooks/usePerformanceScopes';
import * as teamApi from '../api/team';

export default function TeamPage() {
  const { message } = AntApp.useApp();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  // Team & Career is the home of everything about other people. Performance sits
  // one tap down from it, but only for a caller who has someone to look at.
  const perf = usePerformanceScopes();

  useEffect(() => {
    teamApi
      .listTeamTree()
      .then((res) => setData(res?.data ?? res))
      .catch((e) => message.error(e.message));
    /* eslint-disable-next-line */
  }, []);

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <AppBar back title="Team & Career" sub="downline Gen 1–4 · promotion" />

      {!data ? (
        <div style={{ padding: 18 }}><Skeletons n={4} height={80} /></div>
      ) : (
        <div className="scrx" style={{ flex: 1, overflowY: 'auto', padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: 14 }}>
          {/* Promotion roadmap */}
          {(() => {
            const current = data.roadmap?.current ?? 0;
            const target = data.roadmap?.target ?? 0;
            const daysLeft = data.roadmap?.daysLeft ?? 0;
            const pct = target > 0 ? Math.min(100, Math.round((current / target) * 100)) : 0;
            const remaining = Math.max(target - current, 0);
            return (
              <div style={{ background: 'linear-gradient(135deg,#241854,#18345E)', borderRadius: 16, padding: 18, color: '#fff', display: 'flex', flexDirection: 'column', gap: 9 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                  <div style={{ fontSize: 13, fontWeight: 600 }}>Team Leader roadmap</div>
                  <div style={{ fontSize: 11.5, opacity: 0.8 }}>challenge window · {daysLeft} days left</div>
                </div>
                <div style={{ fontSize: 19, fontWeight: 700 }}>{current} / {target} active recruits</div>
                <div style={{ height: 6, borderRadius: 999, background: 'rgba(255,255,255,.18)', overflow: 'hidden' }}>
                  <div style={{ width: `${pct}%`, height: '100%', background: '#5AA1EA' }} />
                </div>
                <div style={{ fontSize: 11.5, opacity: 0.82 }}>
                  {remaining > 0
                    ? `${remaining} more direct active recruit${remaining === 1 ? '' : 's'} (post-April) to qualify for the TL challenge`
                    : 'Recruit target met for the TL challenge'}
                </div>
              </div>
            );
          })()}

          {/* Recruit counts + override rollup */}
          <div style={{ display: 'flex', gap: 12 }}>
            <Stat label="DIRECT ACTIVE" value={data.counts?.directActive ?? 0} />
            <Stat label="POST-APRIL" value={data.counts?.postApril ?? 0} />
            <Stat label="OVERRIDE" value={RM(data.overrideRollup)} tone="var(--ok)" />
          </div>

          {/* Performance entry — production, as opposed to the promotion numbers above */}
          {perf.any && (
            <div
              onClick={() => nav('/team/performance')}
              style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '15px 16px', background: 'var(--surface)', border: '1px solid var(--primary)', borderRadius: 14, cursor: 'pointer' }}
            >
              <span style={{ color: 'var(--primary)', display: 'flex', flex: 'none' }}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="4" y1="20" x2="20" y2="20" /><rect x="6" y="11" width="3.2" height="6" rx="1" /><rect x="12" y="7" width="3.2" height="10" rx="1" /><rect x="17.4" y="13" width="3.2" height="4" rx="1" />
                </svg>
              </span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--primary)' }}>Team performance</div>
                <div style={{ fontSize: 11.5, color: 'var(--muted)' }}>
                  {perf.can.branch ? 'Branch & downline production by period' : 'How your downline is selling, by period'}
                </div>
              </div>
              <span style={{ color: 'var(--muted)', display: 'flex', flex: 'none' }}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 6 15 12 9 18" /></svg>
              </span>
            </div>
          )}

          {/* Hierarchy chart entry — opens the full-screen org chart */}
          <div
            onClick={() => nav('/team/hierarchy')}
            style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '15px 16px', background: 'var(--surface)', border: '1px solid var(--primary)', borderRadius: 14, cursor: 'pointer' }}
          >
            <span style={{ color: 'var(--primary)', display: 'flex', flex: 'none' }}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="9" y="3" width="6" height="5" rx="1" /><rect x="3" y="16" width="6" height="5" rx="1" /><rect x="15" y="16" width="6" height="5" rx="1" />
                <path d="M12 8v4M6 16v-2h12v2" />
              </svg>
            </span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--primary)' }}>View hierarchy chart</div>
              <div style={{ fontSize: 11.5, color: 'var(--muted)' }}>Your downline as an org chart</div>
            </div>
            <span style={{ color: 'var(--muted)', display: 'flex', flex: 'none' }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 6 15 12 9 18" /></svg>
            </span>
          </div>

          {/* Downline */}
          <div style={{ fontSize: 11.5, fontWeight: 600, letterSpacing: '.08em', color: 'var(--muted)' }}>
            DOWNLINE · {(data.downline || []).length} AGENTS
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: -4 }}>
            {(data.downline || []).map((m) => (
              <div key={m.agent_id} style={{ display: 'flex', gap: 14, alignItems: 'center', padding: '15px 16px', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 14 }}>
                <div style={{ width: 36, height: 36, borderRadius: '50%', background: 'var(--chip)', color: 'var(--primary)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11.5, fontWeight: 700, flex: 'none' }}>
                  {initials(m.name)}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 600 }}>{m.name}</div>
                  <div style={{ fontSize: 11.5, color: 'var(--muted)' }}>Gen{m.gen} · {m.tier}% tier</div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: 11, color: 'var(--muted)', fontWeight: 600 }}>GCI</div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--ok)' }}>{RMk(m.gci)}</div>
                </div>
              </div>
            ))}
          </div>

          <div style={{ height: 6 }} />
        </div>
      )}
    </div>
  );
}

const Stat = ({ label, value, tone }) => (
  <div style={{ flex: 1, background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 14, padding: '14px 16px' }}>
    <div style={{ fontSize: 11.5, color: 'var(--muted)', fontWeight: 600, letterSpacing: '.05em' }}>{label}</div>
    <div style={{ fontSize: 15, fontWeight: 700, marginTop: 3, color: tone || 'var(--text)' }}>{value}</div>
  </div>
);
